import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom'

import Layout from './Layout'
import AboutPage from './pages/AboutPage'
import MatchingPage from './pages/MatchingPage'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import { BooleanLogicBuilderPage } from './pages/BooleanLogicBuilderPage'
import DocumentReviewPage from './pages/DocumentReviewPage'
import useAuth from './hooks/useAuth'
import useGearboxData from './hooks/useGearboxData'
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics'
import { ErrorRetry } from './components/ErrorRetry'
import { AdminPage } from './pages/AdminPage'
import { InputFormBuilderPage } from './pages/InputFormBuilderPage'
import { CriteriaAnnotationVerificationPage } from './pages/CriteriaAnnotationVerificationPage'
import { CriteriaValueAssignmentPage } from './pages/CriteriaValueAssignmentPage'

function getBasename(): string {
  let basename = process.env?.PUBLIC_URL || ''

  // If loading via dev.html, adjust basename
  if (window?.location.pathname.startsWith(`${basename}/dev.html`)) {
    basename += '/dev.html'
  }

  return basename
}

function App() {
  const auth = useAuth()
  const gearboxData = useGearboxData(auth)
  const gearboxDataManager = auth.user?.authz['/services/gearbox/data-manager']
  const isAdmin = !!gearboxDataManager && !!gearboxDataManager.length
  const gearboxDataAdmin = auth.user?.authz['/services/gearbox/data-admin']
  const isSuperAdmin = !!gearboxDataAdmin && !!gearboxDataAdmin.length
  const userId = auth.user?.sub ?? ''
  useGoogleAnalytics(userId)

  if (
    auth.loadingStatus === 'not started' ||
    auth.loadingStatus === 'sending'
  ) {
    return <h1>Loading...</h1>
  } else if (auth.loadingStatus === 'error') {
    return <ErrorRetry retry={auth.fetchAuth} />
  }

  return (
    <Router basename={getBasename()}>
      <Layout
        isAuthenticated={auth.isAuthenticated}
        isAdmin={isAdmin}
        username={auth.user?.username ?? ''}
        userId={userId}
        onLogout={auth.signout}
      >
        <Routes>
          <Route
            path="/"
            element={
              !auth.isAuthenticated ? (
                <LandingPage isLLS={false} />
              ) : !auth.isRegistered ? (
                <Navigate to="/register" replace />
              ) : auth.hasDocsToBeReviewed ? (
                <Navigate to="/document-review" replace />
              ) : (
                <MatchingPage {...gearboxData} />
              )
            }
          />
          <Route path="/LLS" element={<LandingPage isLLS={true} />} />
          <Route path="/admin" element={<AdminPage isAdmin={isAdmin} />}>
            <Route
              path="boolean-logic-builder"
              element={
                <BooleanLogicBuilderPage
                  gearboxState={gearboxData.state}
                  isSuperAdmin={isSuperAdmin}
                />
              }
            />
            <Route
              path="input-form-builder"
              element={<InputFormBuilderPage />}
            />
            <Route
              path="criteria-annotation-verification"
              element={<CriteriaAnnotationVerificationPage />}
            />
            <Route
              path="criteria-value-assignment"
              element={<CriteriaValueAssignmentPage />}
            />
          </Route>
          <Route
            path="/login"
            element={
              auth.isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
            }
          />
          <Route
            path="/register"
            element={
              auth.isAuthenticated && !auth.isRegistered ? (
                <RegisterPage
                  docsToBeReviewed={auth.user?.docs_to_be_reviewed ?? []}
                  onRegister={auth.register}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/document-review"
            element={
              auth.hasDocsToBeReviewed ? (
                <DocumentReviewPage
                  docsToBeReviewed={auth.user?.docs_to_be_reviewed ?? []}
                  onReview={auth.reviewDocuments}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </Router>
  )
}

export default App
